import { useEffect } from 'react'
import { dropdownLayerOpen, targetInDropdownLayer } from '../dropdownLayer'

/** Ab hier beginnt das Ziehen der Liste (`useTaskDragAndDrop`, `activationConstraint.distance`). */
const DRAG_START_PX = 5

/**
 * While the entry is expanded/creating, submit the form when the user
 * signals "done" outside of it:
 *
 * - pointerdown anywhere outside the entry (click-away commits, like
 *   most inline editors)
 * - Enter at document level while focus sits on the entry container or
 *   was lost to <body> (e.g. after a dropdown closed)
 *
 * Both stay inert while a floating dropdown is open — confirming a badge
 * dropdown must not also submit and collapse the entry.
 */
export function useSubmitOnOutside(
  entryRef: React.RefObject<HTMLDivElement | null>,
  enabled: boolean,
  handleSubmitRef: React.RefObject<(() => void) | null>,
): void {
  useEffect(() => {
    if (!enabled) return
    let pending: (() => void) | null = null
    const handlePointerDown = (e: PointerEvent) => {
      const target = e.target as Node
      if (entryRef.current?.contains(target)) return
      if (targetInDropdownLayer(e.target)) return
      if (dropdownLayerOpen()) return
      /*
        **Auf einer anderen Aufgabe erst abwarten** (#204, Grill 11): Wer sie zieht, will sie
        vielleicht in DIESE Checkliste einhängen — die Karte muss dafür offen bleiben. Erst
        das Loslassen ohne Bewegung ist ein Klick und schließt wie bisher.
      */
      if ((e.target as HTMLElement).closest?.('[data-task-id]')) {
        pending?.()
        const start = { x: e.clientX, y: e.clientY }
        const onMove = (m: PointerEvent) => {
          if (Math.hypot(m.clientX - start.x, m.clientY - start.y) > DRAG_START_PX) stop()
        }
        const onUp = () => {
          stop()
          handleSubmitRef.current?.()
        }
        const stop = () => {
          document.removeEventListener('pointermove', onMove)
          document.removeEventListener('pointerup', onUp)
          pending = null
        }
        document.addEventListener('pointermove', onMove)
        document.addEventListener('pointerup', onUp)
        pending = stop
        return
      }
      handleSubmitRef.current?.()
    }
    document.addEventListener('pointerdown', handlePointerDown)
    return () => {
      document.removeEventListener('pointerdown', handlePointerDown)
      pending?.()
    }
  }, [enabled, entryRef, handleSubmitRef])

  useEffect(() => {
    if (!enabled) return
    const handleDocumentKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Enter') return
      const target = e.target as HTMLElement
      if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA') return
      if (dropdownLayerOpen()) return
      const isInsideEntry = entryRef.current?.contains(target)
      const isFocusLost = target === document.body || target === document.documentElement
      if (!isInsideEntry && !isFocusLost) return
      e.preventDefault()
      handleSubmitRef.current?.()
    }
    document.addEventListener('keydown', handleDocumentKeyDown)
    return () => document.removeEventListener('keydown', handleDocumentKeyDown)
  }, [enabled, entryRef, handleSubmitRef])
}
